"use client";
import { motion } from "framer-motion";
import { useRef } from "react";
import { fadeUp, stagger } from "@/app/lib/utils/motionVariants";
import { withBasePath, withMediaUrl } from "@/app/lib/basePath";
import Image from "next/image";

export function PlotMapMock() {
  return (
    <Image src={withBasePath('/images/plotter/show-case.webp')} alt="showCase" className="w-full h-full rounded-[8px] object-cover object-center" width={960} height={540} />
  );
}

export default function OverviewSection({ data }: any) {
  if (!data) return null;
  const ref = useRef(null);
  const title = data?.title;
  const titleHighlight = data?.titleHighlight;
  const subtitle = data?.subtitle;
  const mediaType =
    typeof data?.mediaType === "string"
      ? data.mediaType.trim().toLowerCase()
      : "";
  const mediaValue = data?.media ?? data?.image;
  const media = Array.isArray(mediaValue) ? mediaValue[0] : mediaValue;
  //console.log("OverviewSection data:", data);
  const mediaUrl = withMediaUrl(media?.url);
  const embedUrl = data?.embedUrl;
  const isVideo = mediaType === "video";
  const isImage = mediaType === "image" || (!mediaType && Boolean(mediaUrl));
  const isEmbed =
    mediaType === "embedurl" ||
    mediaType === "embed" ||
    (!mediaType && !mediaUrl && Boolean(embedUrl));
  const hasText = typeof data?.text === "string" && data.text.trim().length > 0;
  const hasTitle = typeof title === "string" && title.trim().length > 0;
  const hasTitleHighlight =
    typeof titleHighlight === "string" && titleHighlight.trim().length > 0;
  const hasSubtitle = typeof subtitle === "string" && subtitle.trim().length > 0;
  const titleParts = hasTitleHighlight ? title.split(titleHighlight) : [];
  return (
    <section ref={ref} className="relative py-6 lg:py-8">
      <div className='absolute left-1/2 top-[50%] -translate-x-1/2 -translate-y-1/2 w-[80%] max-w-[400px] aspect-[1/2.2] rounded-[800%] opacity-45 z-[0] blur-[128px] bg-[linear-gradient(#ffffff40,#000fffb3)]'></div>
      <div className="container mx-auto z-1 relative">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger} className="text-center mb-3 lg:mb-9">
          {hasText && (
            <motion.span variants={fadeUp} className="inline-flex common-section-pill mb-2">
              {data.text}
            </motion.span>
          )}
          {hasTitle && (
            <motion.h2 variants={fadeUp} className="section-title">
              {titleParts.length > 1 ? (
                <>
                  {titleParts[0]}
                  <span className="section-highlight-title"> {titleHighlight}</span>
                  {titleParts.slice(1).join(titleHighlight)}
                </>
              ) : (
                title
              )}
            </motion.h2>
          )}
          {hasSubtitle && (
            <motion.p variants={fadeUp} className="mt-3 section-description max-w-4xl mx-auto">
              {subtitle}
            </motion.p>
          )}
        </motion.div>
        <div className="w-full lg:w-[85%] mx-auto">
          {isImage && mediaUrl && (
            <Image
              src={mediaUrl}
              alt={media?.alternativeText ?? "Overview preview"}
              className="h-full w-full object-cover"
              width={1200}
              height={675}
            />
          )}
          {isVideo && mediaUrl && (
            <motion.div className="aspect-[4/3] md:aspect-[16/8] rounded-[14px] overflow-hidden shadow-[0_0_50px_rgba(14,165,233,0.1),0_30px_80px_-20px_rgba(14,165,233,0.25)] bg-black/20 p-[12px] backdrop-blur-[20px] ">
            <video
              className="h-full w-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              >
              <source src={mediaUrl} type={media?.mime ?? "video/mp4"} />
            </video>
              </motion.div>
          )}
          {isEmbed && embedUrl && (
            <motion.div className="aspect-[4/3] md:aspect-[16/8] rounded-[14px] overflow-hidden shadow-[0_0_50px_rgba(14,165,233,0.1),0_30px_80px_-20px_rgba(14,165,233,0.25)] bg-black/20 p-[12px] backdrop-blur-[20px] ">
            <iframe
              className="h-full w-full border-0"
              src={embedUrl}
              title="Overview live project"
            />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
